import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import type { GridRowParams } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import type { InternalAccessUser } from 'src/interfaces/internalAccess'
import { getInternalUsersLoading } from 'src/selectors/internalAccess'
import { getInternalIdentity } from 'src/selectors/internalAuth'
import { EmptyInternalUsers } from './EmptyInternalUsers'
import { buildInternalUserColumns } from './internalUserColumns'

interface Props {
  /** Already filtered by the tab's search. */
  users: InternalAccessUser[]
  onRowClick(user: InternalAccessUser): void
}

const getRowId = (row: InternalAccessUser): string => {
  return row.id
}

const InternalUsersTableComponent = ({ users, onRowClick }: Props) => {
  const identity = useSelector(getInternalIdentity)
  const isLoading = useSelector(getInternalUsersLoading)

  const currentUserId = identity?.internalUserId ?? null

  const columns = useMemo(() => {
    return buildInternalUserColumns(currentUserId)
  }, [currentUserId])

  const handleRowClick = useCallback(
    (params: GridRowParams<InternalAccessUser>) => {
      onRowClick(params.row)
    },
    [onRowClick],
  )

  // No `paginationModel`: every row renders in one scrolling body (the endpoint is unpaginated).
  return (
    <GridTable<InternalAccessUser>
      rows={users}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      fillHeight
      EmptyElement={EmptyInternalUsers}
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      tableDataTestId="internal-users-table"
      rowDataTestIdPrefix="internal-users"
    />
  )
}

export const InternalUsersTable = memo(InternalUsersTableComponent)
